import { isArray } from 'lodash';
import moment from 'moment';
import { capitalize } from 'lodash/fp';

import { ConfigurationAttributesFiltersOptions } from 'store/selectors/app-selectors';
import { dateFormats, getSameYearFormatRange } from 'utils/date/date';
import { LENGTH_OF_STAY_ATTRIBUTE } from 'core/constants/filter-attributes';
import type { Attribute, Attributes } from '@placer-ui/types';

export const NOT_EXIST_CELL_VIEW = '';

export const NA_CELL_VIEW = 'N/A';

type FilterTypes = number[] | string[];

const distanceTypeFormats = {
    '>': 'more than',
    '>=': 'more than',
    '<': 'less than',
    '<=': 'less than',
};

export const EOL = '\r\n';

function createRow(columns: (string | number)[]) {
    const columnsWithoutUndefined = columns.map((colData) => colData ?? '');
    return '"' + columnsWithoutUndefined.join('","') + '"' + EOL;
}

export const createCsvData = (str: string) =>
    new Blob([str], {
        type: 'text/csv;charset=utf-8;',
    });

export function createCsvURL(str: string): string {
    return window.URL.createObjectURL && window.URL.createObjectURL(createCsvData(str));
}

export function buildCSV(headers: Array<string>, csvData: Array<(string | number)[]>) {
    let csv = createRow(headers);

    csvData.forEach((d) => {
        if (headers.length !== d.length) {
            throw new Error('CSV columns do not match');
        }

        csv += createRow(d);
    });

    return csv;
}

function getFilterByName(filterName: string, [item, ...filters]: Attributes) {
    const selectedFilter = filters?.filter((item) => {
        return isArray(item) && item[1] === filterName;
    });

    return selectedFilter[0] || [];
}

function formatDistanceParams(values: Attribute) {
    if (!values.length) return '';

    const sign = values[0] as '<' | '>';
    const filterName = values[1];
    const distance = values[2] as number;

    const typeValue = distanceTypeFormats[sign];
    const filterValue = capitalize(filterName.replace('_', ' '));
    const distanceValue = Math.round(distance / 1610).toFixed(2);

    return `${filterValue} ${typeValue} ${distanceValue}mi`;
}

const formatCsvHeadersDays = (filterAttributes: Attributes) => {
    const filter = getFilterByName('days_of_week', filterAttributes);
    if (!filter.length) return '';

    const values = filter[2] as number[];
    if (values.length) {
        const daysDisplay = values.map((day) => {
            const fixedDay = day < 7 ? day + 1 : 0;
            return moment().day(fixedDay).format('ddd');
        });
        return `${daysDisplay.join(', ')}`;
    }
    return '';
};

const formatCsvHeadersTime = (filterAttributes: Attributes) => {
    const filter = getFilterByName('time', filterAttributes) || [];
    if (!filter.length) return '';

    const values = filter[2] as FilterTypes;
    if (values.length) {
        return `${values.join('-')}`;
    }
    return '';
};

const formatCsvHeadersVisitors = (filterAttributes: Attributes) => {
    const filter = getFilterByName('visits_type', filterAttributes);
    if (!filter.length) return '';

    const values = filter[2] as FilterTypes;
    if (values.length) {
        return `${values.join(', ')}`;
    }
    return '';
};

const formatCsvHeadersHouseholdIncome = (
    filterAttributes: Attributes,
    configurationAttributesFilters: ConfigurationAttributesFiltersOptions,
) => {
    const filter = getFilterByName('household_income', filterAttributes);
    if (!filter.length) return '';

    const values = filter[2] as number[];
    if (values.length) {
        const options = values.map((item, index) => {
            return configurationAttributesFilters.householdIncome[item];
        });
        return `Median HHI ${options.filter((value) => value).join(', ')}`;
    }
    return '';
};

const formatCsvHeaderVisitDuration = (filterAttributes: Attributes) => {
    const filter = getFilterByName(LENGTH_OF_STAY_ATTRIBUTE, filterAttributes);
    if (!filter.length) return '';

    const value = filter[2] as number;
    const sign = filter[0] as '<' | '>';
    const typeValue = distanceTypeFormats[sign] || '';

    return `Length of stay ${typeValue} ${value} min`;
};

const formatCsvHeadersDistance = (filterAttributes: Attributes) => {
    const filterHome = getFilterByName('home_distance', filterAttributes);
    const filterWork = getFilterByName('work_distance', filterAttributes);
    const formattedHomeFilter = formatDistanceParams(filterHome);
    const formattedWorkFilter = formatDistanceParams(filterWork);

    if (filterHome.length || filterWork.length) {
        return {
            formattedHomeFilter,
            formattedWorkFilter,
        };
    }

    return {};
};

export const commonCsvHeaderFilters = (
    configurationAttributesFilters: ConfigurationAttributesFiltersOptions,
    filterAttributes?: Attributes,
) => {
    if (!filterAttributes?.length) return '';

    const selectedDaysOfWeek = formatCsvHeadersDays(filterAttributes);
    const selectedTime = formatCsvHeadersTime(filterAttributes);
    const selectedVisitorsType = formatCsvHeadersVisitors(filterAttributes);
    const selectedVisitDuration = formatCsvHeaderVisitDuration(filterAttributes);
    const { formattedHomeFilter, formattedWorkFilter } = formatCsvHeadersDistance(filterAttributes);
    const selectedHouseholdIncome = formatCsvHeadersHouseholdIncome(
        filterAttributes,
        configurationAttributesFilters,
    );

    const headerArray = [
        selectedDaysOfWeek,
        selectedTime,
        selectedVisitorsType,
        selectedVisitDuration,
        formattedHomeFilter,
        formattedWorkFilter,
        selectedHouseholdIncome,
    ].filter((item) => !!item);

    if (headerArray.length) {
        return `| ${headerArray.join(' | ')}`;
    }
    return '';
};

export const formatCsvHeadersDates = (
    start_date: string,
    end_date: string,
    dateFormat: string = dateFormats.monthDayYearComma,
) => {
    const startDate = moment(start_date).format(dateFormat);
    const endDate = moment(end_date).format(dateFormat);
    return `${startDate} - ${endDate}`;
};

export const formatCsvCompareDates = (
    startDate: string,
    endDate: string,
    comparedStartDate: string,
    comparedEndDate: string,
) => {
    const dateStr = getSameYearFormatRange(startDate, endDate);
    const compareDateStr = getSameYearFormatRange(comparedStartDate, comparedEndDate);
    return `${dateStr} --- ${compareDateStr}`;
};
